import GlobalStyles from '@mui/material/GlobalStyles';
import { Box } from '@qbe/ads-core';
import { AddressSearch, AddressSearchValue } from '@qbe/dfc-smart-ui';
import React from 'react';
import {
  useLazyGetAddressesQuery,
  useRiskRatingLookupMutation,
  useStructuredAddressLookupMutation,
} from 'store';

import AddressValidationAlerts from '~/components-ds/AddressValidationAlerts';
import { useAddressFieldOverrides, useAddressHandlers, useBusinessDetails } from '~/hooks';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';

interface BusinessAddressLookupProps {
  onLoadingChange?: (isLoading: boolean) => void;
}

const BusinessAddressLookup = ({ onLoadingChange }: BusinessAddressLookupProps) => {
  const fieldOverrides = useAddressFieldOverrides();
  const {
    handleAddressChange,
    handleAddressError,
    handleRiskRatingChange,
    handleRiskRatingError,
    handleRiskRatingLoadingChange,
    manualAddressError,
    isError,
    isWarning,
  } = useAddressHandlers({ onLoadingChange });

  const { businessAddress, addressLine1, suburb, state, postcode, isManualEntry } =
    useBusinessDetails();
  useRTKQErrorHandler(manualAddressError);

  const defaultManualAddress: Partial<AddressSearchValue> | null = isManualEntry
    ? {
        streetAddress1: addressLine1,
        city: suburb,
        state: state,
        postcode: postcode,
      }
    : null;

  return (
    <Box display="contents">
      {/* 
        Fix z-index conflict: AddressSearch dropdown was overlapping error modals.
        MUI Autocomplete dropdown defaults to z-index 1300, same as MUI Modal.
        Force dropdown to 1299 so it appears behind DeclineModal and other error modals.
        Applied globally but only affects AddressSearch autocomplete popper elements.
      */}
      <GlobalStyles
        styles={{
          '.MuiAutocomplete-popper': {
            zIndex: '1299 !important',
          },
        }}
      />
      <Box
        width="100%"
        maxWidth={400}
        paddingTop={2}
        sx={{
          // Ensure dropdown stays below modal layers (MUI Modal z-index is 1300)
          '& .MuiAutocomplete-popper': {
            zIndex: 1299,
          },
        }}
      >
        <AddressSearch
          defaultAddress={businessAddress ?? undefined}
          defaultManualAddress={defaultManualAddress}
          fieldName="businessAddress"
          addressApiHooks={{
            useLazyGetAddressesQuery,
            useStructuredAddressLookupMutation,
            useRiskRatingLookupMutation,
          }}
          onChange={handleAddressChange}
          onAddressesError={handleAddressError}
          onRiskRatingChange={handleRiskRatingChange}
          onRiskRatingError={handleRiskRatingError}
          onRiskRatingLoadingChange={handleRiskRatingLoadingChange}
          showManualCountry={false}
          includeRiskRating
          riskRatingRequestMeta={{
            channel: 'QBE Standard',
            product: 'bpk',
            transaction: 'submission',
          }}
          fieldOverrides={fieldOverrides}
          required
        />
      </Box>
      <AddressValidationAlerts isError={isError} isWarning={isWarning} />
    </Box>
  );
};

export default BusinessAddressLookup;
